import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Lock } from 'lucide-react';
import { createLookup, fetchBlock } from '../../shared/api/endpoints';
import { ApiError } from '../../shared/api/client';
import { MASK_CHAR } from '../../shared/lib/mask';

/**
 * The public company page — `/{country}/{identifier}` (section 23.8).
 *
 * This is the product's main free acquisition channel: someone searches a
 * company on Google, lands here, sees enough to trust the service and signs up
 * for the rest. That makes two things load-bearing:
 *
 *   1. **What may be shown is legally constrained.** Registration data about a
 *      legal entity is public and not personal data, so it is shown. Names of
 *      individuals are personal data and are **always** masked here, on every
 *      plan, for every visitor. There is no setting that changes this.
 *   2. **SEO metadata is part of the feature, not decoration.** Without
 *      `schema.org`, canonical URLs, `hreflang` and Open Graph the page will not
 *      be indexed, and an unindexed page earns nothing.
 *
 * The page renders masked content from the start: the server does not send the
 * withheld values at all, so there is nothing to reveal in a devtools panel.
 */

const SUPPORTED = new Set(['br', 'ar', 'cl', 'co', 'mx', 'pe', 'ec', 'uy', 'py', 'bo', 'cr', 'do', 'sv', 'gt', 'hn', 'ni', 'pa']);

export function PublicCompanyPage(): React.ReactNode {
  const { t } = useTranslation();
  const { country, identifier } = useParams<{ country: string; identifier: string }>();
  const countryCode = (country ?? '').toUpperCase();

  const valid =
    SUPPORTED.has(countryCode.toLowerCase()) && Boolean(identifier && identifier.length >= 7);

  const lookup = useQuery({
    queryKey: ['public', countryCode, identifier],
    enabled: valid,
    staleTime: 60 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const created = await createLookup(
        { query: identifier as string, countryCode, idType: idTypeFor(countryCode) },
        { signal },
      );
      // Only the registration and activity blocks are public.
      const [registration, activity, address] = await Promise.all([
        fetchBlock(created.lookupId, 'registration', { signal }).catch(() => null),
        fetchBlock(created.lookupId, 'activity', { signal }).catch(() => null),
        fetchBlock(created.lookupId, 'address', { signal }).catch(() => null),
      ]);
      return { registration, activity, address, lookupId: created.lookupId };
    },
  });

  const registration = lookup.data?.registration?.data as
    | {
        legalName?: string | null;
        tradeName?: string | null;
        status?: string | null;
        registrationDate?: string | null;
        legalForm?: string | null;
        identifiers?: { idType: string; valueMasked: string }[];
      }
    | null
    | undefined;

  const name = registration?.legalName ?? registration?.tradeName ?? identifier;

  // SEO: title, canonical, hreflang, Open Graph and schema.org, all set from the
  // data rather than hardcoded, so each company page describes itself.
  useEffect(() => {
    document.title = name ? `${name} — FOCO` : 'FOCO';

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let element = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta('name', 'description', t('seo.companyDescription', {
      name: name ?? '',
      country: countryCode,
    }));

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/${countryCode.toLowerCase()}/${identifier}`;

    setMeta('property', 'og:title', name ? `${name} — FOCO` : 'FOCO');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', canonical.href);
    setMeta('name', 'twitter:card', 'summary');

    // schema.org: what makes the page eligible for a rich result.
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: registration?.legalName ?? undefined,
      alternateName: registration?.tradeName ?? undefined,
      taxID: registration?.identifiers?.[0]?.valueMasked,
      foundingDate: registration?.registrationDate ?? undefined,
      address: { '@type': 'PostalAddress', addressCountry: countryCode },
    };
    let script = document.getElementById('foco-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.id = 'foco-jsonld';
      (script as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      // The SPA reuses one document, so leaving company metadata behind would
      // mislabel every page visited afterwards.
      document.getElementById('foco-jsonld')?.remove();
    };
  }, [name, countryCode, identifier, registration, t]);

  if (!valid) {
    return <NotFoundBlock />;
  }

  if (lookup.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-sm text-[var(--color-muted-fg)]">{t('common.loading')}</p>
      </div>
    );
  }

  // No data means no page: section 23.8 forbids thin pages in the index, so a
  // company we cannot describe returns a not-found rather than an empty stub.
  if (lookup.isError || !registration) {
    const notFound = lookup.error instanceof ApiError && lookup.error.isNotFound;
    return <NotFoundBlock notFound={notFound} />;
  }

  return (
    <article className="mx-auto max-w-3xl px-4 py-10">
      <header>
        <h1 className="text-2xl font-bold sm:text-3xl">{registration.legalName ?? identifier}</h1>
        {registration.tradeName && (
          <p className="mt-1 text-[var(--color-muted-fg)]">{registration.tradeName}</p>
        )}
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[var(--color-muted-fg)]">
          <span className="inline-flex items-center gap-1">
            <Building2 aria-hidden className="h-4 w-4" />
            {countryCode}
          </span>
          {registration.identifiers?.[0] && (
            <span className="font-mono text-xs">{registration.identifiers[0].valueMasked}</span>
          )}
          {registration.status && <span>{registration.status}</span>}
        </p>
      </header>

      <dl className="mt-6 grid gap-4 sm:grid-cols-2">
        {registration.registrationDate && (
          <Item label={t('blocks.registration.registrationDate')} value={registration.registrationDate} />
        )}
        {registration.legalForm && (
          <Item label={t('blocks.registration.legalForm')} value={registration.legalForm} />
        )}
        <Item
          label={t('blocks.address.city')}
          value={(lookup.data?.address?.data as { city?: string } | null)?.city ?? null}
        />
        <Item
          label={t('blocks.activity.primary')}
          value={(lookup.data?.activity?.data as { primary?: { name?: string } } | null)?.primary?.name ?? null}
        />
      </dl>

      {/* Related persons are shown as a mask and nothing else. This is not a
          paywall: the names are personal data and are not published here at all. */}
      <section className="mt-8 rounded-[var(--radius-card)] border border-[var(--color-border)] p-5">
        <h2 className="flex items-center gap-2 font-semibold">
          <Lock aria-hidden className="h-4 w-4 text-[var(--color-muted-fg)]" />
          {t('publicPage.personsTitle')}
        </h2>
        <p className="mt-2 font-mono text-sm text-[var(--color-muted-fg)]">
          {MASK_CHAR.repeat(34)}
        </p>
        <p className="mt-2 text-xs text-[var(--color-muted-fg)]">
          {t('publicPage.personsNote')}
        </p>
      </section>

      <div className="mt-8 rounded-[var(--radius-card)] border border-brand-600/30 bg-brand-50 p-5 dark:bg-brand-900/20">
        <h2 className="font-semibold">{t('publicPage.ctaTitle')}</h2>
        <p className="mt-1 text-sm text-[var(--color-muted-fg)]">
          {t('publicPage.ctaBody')}
        </p>
        <Link
          to={`/signup?next=/${countryCode.toLowerCase()}/${identifier}`}
          className="mt-3 inline-block rounded-[var(--radius-control)] bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
        >
          {t('publicPage.ctaButton')}
        </Link>
      </div>

      <p className="mt-6 text-xs text-[var(--color-muted-fg)]">
        <Link to="/opt-out" className="hover:text-brand-600 hover:underline">
          {t('publicPage.optOutLink')}
        </Link>
      </p>
    </article>
  );
}

function Item({ label, value }: { label: string; value: string | null }): React.ReactNode {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs text-[var(--color-muted-fg)]">{label}</dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}

function NotFoundBlock({ notFound = true }: { notFound?: boolean }): React.ReactNode {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      {/* `lookup.notFound` is an object (`{ title, body, reason1Title, … }`), so
          the title is addressed explicitly: asking i18next for the parent key
          returns the object and React renders a broken node. */}
      <h1 className="text-xl font-bold">{t('lookup.notFound.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-muted-fg)]">
        {notFound
          ? t('publicPage.notFoundBody')
          : t('errors.generic')}
      </p>
      <Link
        to="/search"
        className="mt-4 inline-block rounded-[var(--radius-control)] bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
      >
        {t('common.search')}
      </Link>
    </div>
  );
}

/** Map a country to its company identifier, for the API path. */
function idTypeFor(countryCode: string): string {
  const map: Record<string, string> = {
    BR: 'cnpj',
    AR: 'cuit',
    CL: 'rut',
    CO: 'nit',
    MX: 'rfc',
    PE: 'ruc',
    EC: 'ruc_ec',
    UY: 'rut',
    PY: 'ruc_py',
    BO: 'nit',
    CR: 'cedula_juridica',
    DO: 'rnc',
    SV: 'dui',
    GT: 'nit',
    HN: 'rtn',
    NI: 'ruc',
    PA: 'ruc',
  };
  return map[countryCode] ?? 'cnpj';
}
